/**
 * Middleware for the voicemail feature.
 *
 * Listens for WebSocket messages from the VRS server related to voicemail
 * and translates them into Redux actions.
 *
 * Also listens for p2p_target_offline messages to show the voicemail prompt.
 */
import { showErrorNotification } from '../notifications/actions';
import logger from '../recording/logger';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { CONFERENCE_JOINED, CONFERENCE_LEFT } from '../base/conference/actionTypes';
import { JitsiRecordingConstants } from '../base/lib-jitsi-meet';
import { FEATURES } from '../base/whitelabel/constants';
import { isFeatureEnabled } from '../base/whitelabel/functions';
import { TRACK_ADDED, TRACK_UPDATED } from '../base/tracks/actionTypes';
import { getLocalVideoTrack } from '../base/tracks/functions.any';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { VOICEMAIL_CAPTURE_REQUESTED, VOICEMAIL_ERROR, VOICEMAIL_RECORDING_STARTED } from './actionTypes';
import { usesServerRecorder } from './capture';
import { dismissPrompt, loadUnreadCount, updateUnreadCount, recordingStarted, recordingComplete, recordingCancelled, removeMessage, showPrompt } from './actions';
let _initialized = false;
let _recordingAttempt;
function asRecord(data) {
    return data && typeof data === 'object' ? data : {};
}
function asString(value) {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
function asNumber(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
MiddlewareRegistry.register(store => next => (action) => {
    const result = next(action);
    // Initialize voicemail WS listeners once
    if (!_initialized && queueService && isFeatureEnabled(FEATURES.VOICEMAIL)) {
        initializeVoicemailListeners(store);
        _initialized = true;
    }
    if (action.type === CONFERENCE_JOINED || action.type === TRACK_ADDED || action.type === TRACK_UPDATED
        || action.type === VOICEMAIL_CAPTURE_REQUESTED || action.type === VOICEMAIL_RECORDING_STARTED) {
        maybeStartVoicemailRoomRecording(store, action.conference || store.getState()['features/base/conference']?.conference);
    }
    if (action.type === CONFERENCE_LEFT && _recordingAttempt && _recordingAttempt.conference === action.conference) {
        const voicemailState = store.getState()['features/voicemail'];
        const messageId = _recordingAttempt?.messageId;
        _recordingAttempt = undefined;
        if (voicemailState?.isRecording && voicemailState.recordingSession?.messageId === messageId) {
            store.dispatch(recordingCancelled());
        }
    }
    return result;
});
function maybeStartVoicemailRoomRecording(store, conference) {
    const state = store.getState();
    const session = state['features/voicemail']?.recordingSession;
    const roomName = state['features/base/conference']?.room;
    if (!state['features/voicemail']?.isRecording || !session?.messageId || !session.roomName
        || session.roomName !== roomName || !conference
        || conference !== state['features/base/conference'].conference) {
        return;
    }
    if (_recordingAttempt?.conference !== conference || _recordingAttempt?.messageId !== session.messageId) {
        _recordingAttempt = { conference, messageId: session.messageId, started: false };
    }
    const attempt = _recordingAttempt;
    const localVideoTrack = getLocalVideoTrack(state['features/base/tracks']);
    // Where the browser can record the take itself, it does, and the
    // server-side recorder is never asked to join. See capture.web.ts.
    if (!usesServerRecorder()) {
        return;
    }
    // The recorder starts when the person presses record, not when their
    // camera happens to become ready: otherwise every take opens with several
    // seconds of someone waiting for the light to come on.
    if (!state['features/voicemail']?.isCaptureRequested) {
        return;
    }
    if (attempt.started || !localVideoTrack?.jitsiTrack || localVideoTrack.muted
        || typeof conference.startRecording !== 'function') {
        return;
    }
    const recordingFailed = () => {
        const current = store.getState();
        // A settled vendor promise belongs to the room/session that started it.
        if (_recordingAttempt !== attempt || current['features/base/conference'].conference !== conference
            || current['features/voicemail'].recordingSession?.messageId !== session.messageId) {
            return;
        }
        _recordingAttempt = undefined;
        logger.warn('VideoMail recording failed to start');
        store.dispatch({ type: VOICEMAIL_ERROR, error: 'VideoMail recording failed to start. Please try again.' });
        store.dispatch(showErrorNotification({
            descriptionKey: 'recording.error',
            titleKey: 'recording.failedToStart'
        }));
    };
    attempt.started = true;
    try {
        // Narrow the vendor's untyped return locally; do not let it enter app state.
        const result = conference.startRecording({
            mode: JitsiRecordingConstants.mode.FILE,
            appData: JSON.stringify({
                file_recording_metadata: {
                    share: false,
                    videomailKind: session.kind || 'message',
                    voicemailMessageId: session.messageId
                }
            })
        });
        void Promise.resolve(result).catch(recordingFailed);
    }
    catch {
        recordingFailed();
    }
}
function showVoicemailPrompt(store, data) {
    const payload = asRecord(data);
    const calleeName = asString(payload.calleeName);
    const calleePhone = asString(payload.calleePhone);
    const calleeId = asString(payload.calleeId);
    const greetingRecord = asRecord(payload.greeting);
    const greetingId = asString(greetingRecord.id);
    if (payload.voicemailAvailable === true && calleeName && (calleePhone || calleeId)) {
        store.dispatch(showPrompt({
            calleeName,
            calleePhone,
            calleeId,
            deliveryReason: asString(payload.deliveryReason),
            reason: asString(payload.reason),
            greeting: greetingId ? {
                durationSeconds: asNumber(greetingRecord.durationSeconds),
                id: greetingId,
                playbackUrl: asString(greetingRecord.playbackUrl),
                status: 'available',
                thumbnailUrl: asString(greetingRecord.thumbnailUrl)
            } : null
        }));
    }
}
/**
 * Wire up WebSocket events from the InterpreterQueueService
 * to Redux dispatches for voicemail.
 */
function initializeVoicemailListeners(store) {
    if (!queueService?.on) {
        return;
    }
    // New voicemail message notification
    queueService.on('voicemail_new_message', (data) => {
        const payload = asRecord(data);
        const count = asNumber(payload.count);
        if (count !== undefined) {
            store.dispatch(updateUnreadCount(count));
        }
        else {
            store.dispatch(loadUnreadCount());
        }
    });
    // Unread count push
    queueService.on('voicemail_unread_count', (data) => {
        const count = asNumber(asRecord(data).count);
        if (count !== undefined) {
            store.dispatch(updateUnreadCount(count));
        }
    });
    // Recording started confirmation
    queueService.on('voicemail_recording_started', (data) => {
        const payload = asRecord(data);
        const messageId = asString(payload.messageId);
        const roomName = asString(payload.roomName);
        if (!messageId || !roomName) {
            return;
        }
        store.dispatch(recordingStarted({
            messageId,
            roomName,
            maxDurationSeconds: asNumber(payload.maxDurationSeconds) || 180
        }));
    });
    // Recording complete
    queueService.on('voicemail_recording_complete', (data) => {
        const payload = asRecord(data);
        const messageId = asString(payload.messageId);
        const durationSeconds = asNumber(payload.durationSeconds);
        const kind = asString(payload.kind) === 'greeting' ? 'greeting' : 'message';
        if (messageId && durationSeconds !== undefined) {
            store.dispatch(recordingComplete(messageId, durationSeconds, kind));
        }
    });
    // Recording cancelled
    queueService.on('voicemail_recording_cancelled', (data) => {
        const messageId = asString(asRecord(data).messageId);
        if (messageId && store.getState()['features/voicemail'].recordingSession?.messageId === messageId) {
            _recordingAttempt = undefined;
            store.dispatch(recordingCancelled());
        }
    });
    // Message deleted
    queueService.on('voicemail_message_deleted', (data) => {
        const messageId = asString(asRecord(data).messageId);
        if (messageId) {
            store.dispatch(removeMessage(messageId));
        }
    });
    // Error
    queueService.on('voicemail_error', (data) => {
        store.dispatch({
            type: VOICEMAIL_ERROR,
            error: asString(asRecord(data).message) || 'Voicemail error'
        });
    });
    // P2P/VRS target unavailable → show voicemail prompt
    queueService.on('p2p_target_offline', (data) => {
        showVoicemailPrompt(store, data);
    });
    queueService.on('p2pTargetDnd', (data) => {
        showVoicemailPrompt(store, { ...asRecord(data), reason: asString(asRecord(data).reason) || 'do_not_disturb' });
    });
    // The person we just failed to reach is calling back: their call wins
    // over the VideoMail offer. Only THEIR call — anyone else ringing in
    // while the prompt is up is handled as busy by the incoming-call sheet.
    queueService.on('p2pIncomingCall', (data) => {
        const voicemailState = store.getState()['features/voicemail'];
        const callerId = asString(asRecord(data).callerId);
        if (voicemailState?.isPromptVisible && callerId && callerId === voicemailState.promptData?.calleeId) {
            store.dispatch(dismissPrompt());
        }
    });
    // Joining any room (answered callback, redial) retires the prompt.
    queueService.on('p2pJoinRoom', () => {
        if (store.getState()['features/voicemail']?.isPromptVisible) {
            store.dispatch(dismissPrompt());
        }
    });
    queueService.on('vrsClientUnavailable', (data) => {
        showVoicemailPrompt(store, data);
    });
    console.log('[Voicemail] Middleware initialized');
}
